import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, RefreshCw, X, Check } from 'lucide-react'
import { DEFAULT_PRESET_IDS } from './constants'

export function PresetEditor({ quizIds, onApply, isLoading }: {
  quizIds: string[]
  onApply: (ids: string[]) => void
  isLoading: boolean
}) {
  const { t } = useTranslation('admin')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(quizIds.join('\n'))

  const handleEdit = () => {
    setDraft(quizIds.join('\n'))
    setEditing(true)
  }

  const handleCancel = () => setEditing(false)

  const handleApply = () => {
    const ids = draft
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    if (ids.length === 0) return
    onApply(ids)
    setEditing(false)
  }

  const handleReset = () => {
    onApply([...DEFAULT_PRESET_IDS])
    setEditing(false)
  }

  return (
    <div className="overflow-hidden w-64 bg-white rounded-lg border border-gray-200">
      <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">{t('print.quizId.title')}</h3>
        <div className="flex gap-1 items-center">
          {!editing && (
            <>
              <button
                onClick={handleEdit}
                className="text-gray-400 transition-colors hover:text-gray-600"
                title={t('print.quizId.edit')}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => onApply(quizIds)}
                disabled={isLoading}
                className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
                title={t('print.quizId.refresh')}
              >
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="p-3 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            placeholder={t('print.quizId.placeholder')}
            className="w-full font-mono text-xs rounded-xs border-gray-300 shadow-2xs focus:border-primary-500 focus:ring-primary-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleApply}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-primary-500 rounded-xs hover:bg-primary-600 transition-colors"
            >
              <Check size={12} />
              {t('print.quizId.apply')}
            </button>
            <button
              onClick={handleReset}
              className="px-2 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-xs hover:bg-gray-50 transition-colors"
            >
              {t('print.quizId.defaults')}
            </button>
            <button
              onClick={handleCancel}
              className="px-2 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-xs hover:bg-gray-50 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      ) : (
        <div className="p-3 space-y-1">
          {quizIds.map((id, i) => (
            <div key={i} className="flex gap-2 items-center text-xs">
              <span className="w-4 text-right text-gray-400 shrink-0">{i + 1}.</span>
              <span className="font-mono text-gray-700 truncate">{id}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
